import { Box, Paper, IconButton, Toolbar, Typography, Tooltip, Fab } from "@mui/material";
import { 
    NavigateBefore, 
    NavigateNext, 
    LightMode, 
    DarkMode, 
    Download,
    MenuBook,
    Close,
    ExpandMore,
    ArrowLeft
} from "@mui/icons-material";
import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect, useState } from "react";
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Youtube from '@tiptap/extension-youtube';
import Code from '@tiptap/extension-code';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import CharacterCount from '@tiptap/extension-character-count';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import { CustomizedFontSize } from "./components/font-size";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import BackdropLoader from "../../../../../components/backdrop-loader";
import './editor-style.css';
import saveNotePDF from "./utils/save-note-pdf";
import { createLowlight } from "lowlight";

interface Page {
    id: number;
    content: any; 
    title: string;
}

export default function NoteReader() {
    const [mode, setMode] = useState<number>(() => {
        const saved = localStorage.getItem("noteReaderTheme");
        return saved ? JSON.parse(saved) : 1;
    });    
    const [currentPageIndex, setCurrentPageIndex] = useState(0);
    
    const [toolbarMinimized, setToolbarMinimized] = useState<boolean>(() => {
        const saved = localStorage.getItem("toolbarMinimized");
        return saved ? JSON.parse(saved) : false; 
    });


    const { classroom_id, collection_id } = useParams<{
        classroom_id: string;
        collection_id: string;
    }>();

    const [searchParams] = useSearchParams();

    const note_id = searchParams.get("note_id");

    const [pages, setPages] = useState<Page[] | null>(null);
    
    const lowlight = createLowlight();

    const editorExtensions = [
        StarterKit.configure({
            blockquote: false,
            codeBlock: false,
            code: false,
        }),
        TextStyle.configure({
            mergeNestedSpanStyles: true
        }),
        Blockquote.configure({
            HTMLAttributes: {
                class: 'blockquote'
            }
        }),
        Color,
        Underline,
        Link.configure({ openOnClick: false }),
        Image.configure({
            HTMLAttributes: {
                class: 'image'
            }
        }),
        Table.configure({
            resizable: true,
            allowTableNodeSelection: true,
            HTMLAttributes: {
                class: 'table'
            }
        }),
        TableRow,
        TableCell,
        TableHeader,
        CodeBlockLowlight.configure({
            lowlight,
            languageClassPrefix: 'language-',
            HTMLAttributes: {
                class: 'codeblock'
            },
            defaultLanguage: null,
        }),
        Youtube.configure({
            controls: false,
            nocookie: true,
            HTMLAttributes: {
                class: 'youtube'
            }
        }),
        Code.configure({
        HTMLAttributes: {
            class: 'code'
        }
        }), 
        Highlight.configure({
            multicolor: true,
            HTMLAttributes: {
                class: 'highlight'
            }
        }),
        Subscript,
        Superscript,
        CharacterCount,
        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
        FontFamily.configure({
            types: ['textStyle']
        }),
        CustomizedFontSize,
    ];

    const editor = useEditor({
        extensions: editorExtensions,
        editable: false,
        editorProps: {
            attributes: {
                class: mode === 1 ? 'editor-light' : 'editor-dark'
            },
        },
    });

    useEffect(() => {
        const loadPages = async () => {
            const response = await axios.get<Page[]>(
                `https://d1at3arxf72ma9.cloudfront.net/classrooms/${classroom_id}/resources/${note_id}`
            );
            setPages(response.data);
            editor?.commands.setContent(response.data[0].content, false);
        };

        loadPages();
    }, [note_id]);

    useEffect(() => {
        if (pages && editor && pages[currentPageIndex]) {
            editor.commands.setContent(pages[currentPageIndex].content, false);
        }
    }, [currentPageIndex, pages, editor]);

    const toggleMode = () => {
        const newTheme = mode === 1 ? 2 : 1;
        setMode(newTheme);
        localStorage.setItem("noteReaderTheme", JSON.stringify(newTheme));
    };

    const goToPreviousPage = () => {
        if (currentPageIndex > 0) {
            setCurrentPageIndex(currentPageIndex - 1);
        }
    };

    const goToNextPage = () => {
        if (pages && currentPageIndex < pages.length - 1) {
            setCurrentPageIndex(currentPageIndex + 1);
        }
    };

    const downloadNote = async () => {
        if (!pages || !editor) return;

        await saveNotePDF(pages, note_id!, editorExtensions);
    };

    const toggleToolbar = () => {
        setToolbarMinimized(prev => {
            const newState = !prev;
            localStorage.setItem("toolbarMinimized", JSON.stringify(newState));
            return newState;
        });
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                goToPreviousPage();
                event.preventDefault();
            } else if (event.key === 'ArrowRight') {
                goToNextPage();
                event.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [currentPageIndex, pages]);


    if (!pages || !editor) {
        return <BackdropLoader/>
    }

    return (
        <Box 
            sx={{
                backgroundColor: mode === 1 ? '#c6e1f5' : "#121212",
            }}
        >
            {/* Toolbar - Full or Minimized */}
            <Paper
                elevation={6}
                sx={{
                    position: 'fixed',
                    top: 20,
                    right: 10,
                    zIndex: 1000,
                    borderRadius: 3,
                    backgroundColor: mode === 1 ? 'white' : '#2d2d2d',
                    border: mode === 1 ? '1px solid #e0e0e0' : '1px solid #404040',
                    transition: 'all 0.3s ease-in-out',
                    overflow: 'hidden'
                }}
            >
                {toolbarMinimized ? (
                    <Tooltip title="Expand Toolbar">
                        <IconButton 
                            onClick={toggleToolbar}
                            sx={{ 
                                color: mode === 1 ? '#1976d2' : '#90caf9',
                                p: 1.5,
                                // '&:hover': {
                                //     backgroundColor: mode === 1 ? '#f5f5f5' : '#404040'
                                // }
                            }}
                        >
                            <ArrowLeft />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Toolbar 
                        variant="dense" 
                        sx={{ 
                            minHeight: 48,
                            padding: '0 8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <Box 
                            sx={{ 
                                width: 1, 
                                height: 20, 
                                backgroundColor: mode === 1 ? '#e0e0e0' : '#404040',
                                mx: 0.5
                            }} 
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Previous Page">
                                <span>
                                    <IconButton 
                                        onClick={goToPreviousPage}
                                        disabled={currentPageIndex === 0}
                                        size="small"
                                        sx={{ 
                                            color: mode === 1 ? '#1976d2' : '#90caf9',
                                            '&:disabled': { 
                                                color: mode === 1 ? '#ccc' : '#666' 
                                            }
                                        }}
                                    >
                                        <NavigateBefore />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: mode === 1 ? '#666' : '#ccc',
                                    minWidth: 40,
                                    textAlign: 'center',
                                    fontSize: '0.75rem'
                                }}
                            >
                                {currentPageIndex + 1} / {pages.length}
                            </Typography>
                            
                            <Tooltip title="Next Page">
                                <span>
                                    <IconButton 
                                        onClick={goToNextPage}
                                        disabled={currentPageIndex === pages.length - 1}
                                        size="small"
                                        sx={{ 
                                            color: mode === 1 ? '#1976d2' : '#90caf9',
                                            '&:disabled': { 
                                                color: mode === 1 ? '#ccc' : '#666' 
                                            }
                                        }}
                                    >
                                        <NavigateNext />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>

                        <Box 
                            sx={{ 
                                width: 1, 
                                height: 24, 
                                backgroundColor: mode === 1 ? '#e0e0e0' : '#404040',
                                mx: 1
                            }} 
                        />

                        <Tooltip title={mode === 1 ? "Switch to Dark Mode" : "Switch to Light Mode"}>
                            <IconButton 
                                onClick={toggleMode}
                                size="small"
                                sx={{ 
                                    color: mode === 1 ? '#1976d2' : '#90caf9'
                                }}
                            >
                                {mode === 1 ? <DarkMode /> : <LightMode />}
                            </IconButton>
                        </Tooltip>

                        {/* Download */}
                        <Tooltip title="Download Note">
                            <IconButton 
                                onClick={downloadNote}
                                size="small"
                                sx={{ 
                                    color: mode === 1 ? '#1976d2' : '#90caf9'
                                }}
                            >
                                <Download />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Minimize Toolbar">
                            <IconButton 
                                onClick={toggleToolbar}
                                sx={{ 
                                    color: mode === 1 ? '#666' : '#999',
                                    fontSize: '5px'
                                    // '&:hover': {
                                    //     color: mode === 1 ? '#1976d2' : '#90caf9',
                                    //     backgroundColor: mode === 1 ? '#f5f5f5' : '#404040'
                                    // }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                )}
            </Paper>

            <Box
                sx={{
                    padding: 2,
                }}
            >
                <Paper
                    sx={{
                        marginX: 'auto',
                        minHeight: '100vh',
                        maxWidth: 'md',
                        padding: "0.3in",
                        border: "1px solid #9d9e9e",
                        position: 'relative' as const,
                        pointerEvents: 'auto' as const,
                        backgroundColor: mode === 1 ? 'white' : '#121212',
                    }}      
                >
                    {/* {pages[currentPageIndex]?.title && (
                        <Typography 
                            variant="h5" 
                            component="h1" 
                            sx={{ 
                                mb: 3,
                                color: mode === 1 ? '#333' : '#fff',
                                fontWeight: 600,
                                borderBottom: mode === 1 ? '2px solid #e0e0e0' : '2px solid #404040',
                                paddingBottom: 1
                            }}
                        >
                            <MenuBook sx={{ mr: 1, verticalAlign: 'middle' }} />
                            {pages[currentPageIndex].title}
                        </Typography>
                    )} */}

                    <EditorContent editor={editor}/>

                </Paper>
            </Box>
        </Box>
    );
}